import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Button, Dialog, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import IconButton from 'components/@extended/IconButton';
import CustomTooltip from 'components/CustomTooltip';
import ActionConfirmation from 'components/mca/ActionConfirmation';
import AddMerchantBankAccount from 'sections/merchants/AddMerchantBankAccount';
import BankInfoCell, { BankInfoCellData } from 'components/ag-grid/BankInfoCell';
import accountCellRenderer, { formatAccountNumber } from 'components/ag-grid/AgGridAccountCellRenderer';
import { DefaultAgGridTooltip } from 'components/ag-grid/DefaultAgGridTooltip';
import useConfig from 'hooks/useConfig';
import {
  Company, MerchantBankAccount, User, UserBankAccount,
  useBankByRoutingNumberLazyQuery,
  useDeleteMerchantBankAccountMutation, useDeleteUserBankAccountMutation,
  useMerchantBankAccountsQuery,
  useSaveMerchantBankAccountMutation, useSaveUserBankAccountMutation,
  useUserBankAccountsQuery
} from 'graphql/types';
import { openSnackbar } from 'store/reducers/snackbar';
import { dispatch } from 'store';
import 'ag-grid-enterprise/styles/ag-grid.css';
import 'ag-grid-enterprise/styles/ag-theme-material.css';
import 'assets/third-party/ag-theme-material-dark.scss';

type BankRow = {
  id: string;
  routingNumber?: string | null;
  accountNumber?: string | null;
  name?: string | null;
};

type DraftRow = {
  draftIdx: number;
  routingNumber: string;
  accountNumber: string;
  name?: string | null;
};

const notify = (message: string, color: 'success' | 'error') =>
  dispatch(openSnackbar({ open: true, message, variant: 'alert', alert: { color }, close: true }));

const defaultColDef: ColDef = {
  editable: false,
  sortable: true,
  resizable: true,
  suppressHeaderMenuButton: true,
  filter: false,
  tooltipComponent: DefaultAgGridTooltip
};

const RoutingBankCell = ({ routingNumber }: { routingNumber?: string | null }) => {
  const [fetchBankByRoutingNumber] = useBankByRoutingNumberLazyQuery({ fetchPolicy: 'cache-and-network' });
  const [data, setData] = useState<BankInfoCellData | null>(null);

  useEffect(() => {
    if (!routingNumber?.trim()) { setData(null); return; }
    let cancelled = false;
    fetchBankByRoutingNumber({ variables: { routingNumber } }).then((res) => {
      if (cancelled) return;
      const bank = res.data?.bankByRoutingNumber;
      setData(bank ? { bankName: bank.bankName, bankLogo: bank.logo || null, locationName: bank.locationName || '' } : null);
    });
    return () => { cancelled = true; };
  }, [routingNumber, fetchBankByRoutingNumber]);

  return <BankInfoCell data={data} showUnknown inGrid />;
};

export type BankInfoTabContentProps =
  ({ variant: 'user'; user?: User | null } | { variant: 'merchant'; merchant?: Company | null });

export type BankInfoTabContentHandle = {
  persistDrafts: (entityId: string) => Promise<void>;
};

const BankInfoTabContent = forwardRef<BankInfoTabContentHandle, BankInfoTabContentProps>(function BankInfoTabContent(props, ref) {
  const theme = useTheme();
  const { mode } = useConfig();
  const ownerId = props.variant === 'user' ? props.user?.id : props.merchant?.id;
  const variant = props.variant;

  const draftCounter = useRef(0);
  const [draftRows, setDraftRows] = useState<DraftRow[]>([]);
  const draftRowsRef = useRef<DraftRow[]>([]);
  useEffect(() => { draftRowsRef.current = draftRows; }, [draftRows]);

  const [selected, setSelected] = useState<BankRow | DraftRow | null>(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  const userQuery = useUserBankAccountsQuery({
    skip: variant !== 'user' || !ownerId,
    fetchPolicy: 'network-only',
    variables: { userId: ownerId! }
  });
  const merchantQuery = useMerchantBankAccountsQuery({
    skip: variant !== 'merchant' || !ownerId,
    fetchPolicy: 'network-only',
    variables: { merchantId: ownerId! }
  });

  const refetch = variant === 'user' ? userQuery.refetch : merchantQuery.refetch;

  const serverRows: BankRow[] = useMemo(() => {
    const accounts =
      variant === 'user'
        ? (userQuery.data?.userBankAccounts ?? [])
        : (merchantQuery.data?.merchantBankAccounts ?? []);
  
    return accounts.map(a => ({
      id: a.id || '',
      routingNumber: a.routingNumber,
      accountNumber: a.accountNumber,
      name: a?.name,
    }));
  }, [variant, userQuery.data, merchantQuery.data]);

  const [deleteUserBankAccount] = useDeleteUserBankAccountMutation();
  const [deleteMerchantBankAccount] = useDeleteMerchantBankAccountMutation();
  const [saveUserBankAccount] = useSaveUserBankAccountMutation();
  const [saveMerchantBankAccount] = useSaveMerchantBankAccountMutation();


  const persistDrafts = useCallback(async (entityId: string) => {
    const pending = draftRowsRef.current.filter(r => r.routingNumber?.trim() && r.accountNumber?.trim());
    if (!pending.length) return;
    try {
      for (const row of pending) {
        const dto = { id: null, routingNumber: row.routingNumber, accountNumber: row.accountNumber };
        if (variant === 'user') await saveUserBankAccount({ variables: { userId: entityId, dto } });
        else await saveMerchantBankAccount({ variables: { merchantId: entityId, dto } });
      }
      setDraftRows([]);
      notify('Bank accounts saved.', 'success');
    } catch {
      notify('User or company was saved, but one or more bank accounts could not be saved.', 'error');
    }
  }, [variant, saveUserBankAccount, saveMerchantBankAccount]);

  useImperativeHandle(ref, () => ({ persistDrafts }), [persistDrafts]);

  const handleDelete = async (confirmed: boolean) => {
    if (!confirmed || !selected) { setOpenDelete(false); return; }

    if (selected && 'draftIdx' in selected) {
      setDraftRows(prev => prev.filter(r => r.draftIdx !== selected.draftIdx));
      setOpenDelete(false);
      setSelected(null);
      return;
    }

    if (!selected.id) { setOpenDelete(false); return; }

    try {
      if (variant === 'user') await deleteUserBankAccount({ variables: { id: selected.id } });
      else await deleteMerchantBankAccount({ variables: { id: selected.id } });
      await refetch();
      notify('Bank Account deleted successfully.', 'success');
    } catch {
      notify('Failed to delete bank account.', 'error');
    } finally {
      setOpenDelete(false);
      setSelected(null);
    }
  };

  const handleBankFormSaved = useCallback((payload: Partial<BankRow> | null | unknown) => {
    setFormOpen(false);
    if (!ownerId) {
      const p = payload as Partial<BankRow> | null;
      if (!p?.routingNumber || !p?.accountNumber) return;
      if (selected && 'draftIdx' in selected) {
        setDraftRows(prev => prev.map(r =>
          r.draftIdx === selected.draftIdx
            ? { ...r, routingNumber: p.routingNumber!, accountNumber: p.accountNumber!, name: p.name || r.name }
            : r
        ));
      } else {
        setDraftRows(prev => [...prev, {
          draftIdx: draftCounter.current++,
          routingNumber: p.routingNumber!,
          accountNumber: p.accountNumber!,
          name: p.name
        }]);
      }
      return;
    }
    refetch();
  }, [ownerId, selected, refetch]);

  const gridRows = useMemo(() => {
    const rows = ownerId ? serverRows : draftRows.map(({ draftIdx: _, ...r }) => ({ ...r, id: '' }));
    return rows.filter(r => r.accountNumber?.trim());
  }, [ownerId, draftRows, serverRows]);

  const columnDefs = useMemo<ColDef<BankRow>[]>(
    () => [
      {
        headerName: 'Bank',
        colId: 'bank',
        headerClass: 'limegreen',
        sortable: true,
        width: 300,
        cellRenderer: (params: ICellRendererParams<BankRow>) => <RoutingBankCell routingNumber={params.data?.routingNumber} />
      },
      {
        headerName: 'Routing #',
        field: 'routingNumber',
        headerClass: 'turquoise',
        width: 150,
        tooltipValueGetter: (p) => p.data?.routingNumber || ''
      },
      {
        headerName: 'Account #',
        field: 'accountNumber',
        headerClass: 'ag-header-cell-cash-advance-group-right',
        width: 150,
        cellRenderer: accountCellRenderer,
        tooltipValueGetter: (p) => formatAccountNumber(p.data?.accountNumber)
      },
      {
        headerName: '',
        colId: 'actions',
        lockVisible: true,
        sortable: false,
        resizable: false,
        suppressNavigable: true,
        suppressColumnsToolPanel: true,
        enablePivot: false,
        pivot: false,
        aggFunc: null,
        cellClass: 'no-border',
        minWidth: 100,
        maxWidth: 100,
        cellRenderer: (params: ICellRendererParams<BankRow>) => {
          if (!params.data) return null;
          return (
            <Stack direction="row" alignItems="center" justifyContent="center" height="100%" spacing={0.5}>
              <span className="hover-action-content" style={{ display: 'flex', alignItems: 'center' }}>
                <CustomTooltip title="Edit Bank Account">
                  <IconButton
                    sx={{ color: theme.palette.lightgreen?.main }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const draft = !ownerId
                        ? draftRowsRef.current.find(
                            (d) => d.accountNumber === params.data!.accountNumber && d.routingNumber === params.data!.routingNumber
                          )
                        : null;
                      setSelected(draft ?? params.data!);
                      setFormOpen(true);
                    }}
                  >
                    <EditOutlined />
                  </IconButton>
                </CustomTooltip>
              </span>
              <span className="hover-action-content" style={{ display: 'flex', alignItems: 'center' }}>
                <CustomTooltip title="Delete Bank Account">
                  <IconButton
                    sx={{ color: theme.palette.crimson?.main }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const draft = !ownerId
                        ? draftRowsRef.current.find(
                            (d) => d.accountNumber === params.data!.accountNumber && d.routingNumber === params.data!.routingNumber
                          )
                        : null;
                      setSelected(draft ?? params.data!);
                      setOpenDelete(true);
                    }}
                  >
                    <DeleteOutlined />
                  </IconButton>
                </CustomTooltip>
              </span>
            </Stack>
          );
        }
      }
    ],
    [theme]
  );

  return (
    <>
      <Stack spacing={1} sx={{ p: 1 }}>
        <Stack direction="row" justifyContent="flex-end" alignItems="center">
          <CustomTooltip title="Add Bank Account" placement="top">
            <Button variant="contained" onClick={() => { setSelected(null); setFormOpen(true); }} sx={{ fontSize: '12px', minWidth: 'auto' }}>
              <PlusOutlined />
            </Button>
          </CustomTooltip>
        </Stack>
        <div className={mode === 'light' ? 'ag-theme-material' : 'ag-theme-material-dark'} style={{ height: 280, width: '100%' }}>
          <AgGridReact<BankRow>
            rowData={gridRows}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            singleClickEdit={false}
            suppressContextMenu
            suppressCellFocus
            rowHeight={40}
            headerHeight={40}
            noRowsOverlayComponent={() => (
              <Typography variant="body2" color="text.secondary">
                No bank accounts. Click + to add one.
              </Typography>
            )}
          />
        </div>
      </Stack>

      <Dialog maxWidth="sm" fullWidth onClose={() => setFormOpen(false)} open={formOpen} sx={{ '& .MuiDialog-paper': { p: 0, maxWidth: 550 } }}>
        {formOpen && (
          <AddMerchantBankAccount
            {...(variant === 'user' ? { userId: ownerId || undefined } : { merchant: props.merchant || undefined })}
            deferPersistence={!ownerId}
            entity={selected && 'draftIdx' in selected
              ? { id: '', routingNumber: selected.routingNumber, accountNumber: selected.accountNumber, name: selected.name } as BankRow
              : selected}
            onCancel={() => setFormOpen(false)}
            onSave={handleBankFormSaved}
          />
        )}
      </Dialog>

      <ActionConfirmation
        title="Deleted bank account details cannot be restored. Would you like to proceed?"
        type="error"
        useYesNoButtonLabels
        open={openDelete}
        handleClose={handleDelete}
      />
    </>
  );
});

export default BankInfoTabContent;